import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
  createSearchParams,
  useLocation,
} from "react-router-dom"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { FormattedMessage, useIntl } from "react-intl"
import classNames from "classnames"

// Hooks
import { useData } from "../../../../hooks/useData";
import { useJobInfo } from "../../../../hooks/useJobInfo";

// Utils
import { fromNow } from "../../../../util/util"

// Components
import Spinner from "../../Spinner"
import { CustomLink, CustomNavLink, Checkbox, ConfirmationDialog } from "../../index"

// Services
import { deleteJobs } from "../../../../services/jobs"

// Redux
import { removeJob } from "../../../../redux/slices/jobSlice"

// Constants
import { GA_EVENTS } from "../../../../constants/analytics"
import { JOB_STATUS } from "../../../../constants"

const ACTION_ITEMS = [
  {
    name: "sidebar.match",
    key: "job-match",
  },
  {
    name: "common.profile",
    key: "profile",
  },
  {
    name: "common.cv",
    key: "cv",
  },
];

const MatchesPageSidebar = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()
  const { jobId } = useParams()
  const tab = searchParams.get("tab") || "job-match"
  const selectedCandidate = searchParams.get("candidate")

  const [search, setSearch] = useState("")
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [selectedJobs, setSelectedJobs] = useState(new Set())
  const [isDeletingJobs, setIsDeletingJobs] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const today = new Date().toISOString().split("T")[0]

  const {
    jobStatus,
    candidates,
    selectedCVs,
  } = useSelector((state) => state.jobs, shallowEqual)
  const teamLanguage = useSelector((state) => state.auth.team.language);

  // Data hooks
  const { cvs, jobs: storedJobs, isFetching } = useData({});
  const { isFetching: isFetchingJobInfo } = useJobInfo(jobId, selectedCandidate);

  // Navigate to first job if no job is selected
  useEffect(() => {
    if (!jobId && storedJobs.length) {
      const mostRecentJob = storedJobs[storedJobs.length - 1]
      navigate(`/job-match/${mostRecentJob.id}/matches`)
    }
  }, [jobId, storedJobs, navigate])

  const selectedJob = storedJobs.find((job) => job.id === jobId)


  // #region ------------- Render Components -------------

  const sideBarCandidates = selectedJob && candidates?.length && (
    <div className="matches__candidates">
      {candidates.map((candidate) => {
        const selected =
          candidate.id === selectedCandidate && pathname.includes("/matches")
        const candidateCV = candidate?.cv || cvs?.find((cv) => cv.id === candidate.id)

        const onCandidateClick = () =>
          navigate({
            pathname: `/job-match/${jobId}/matches`,
            search: createSearchParams({
              tab: "job-match",
              candidate: candidate.id,
            }).toString(),
          })

        const actionButtons = selected && (
          <div className="matches__candidate-actions">
            {ACTION_ITEMS.slice(0, candidateCV ? undefined : 1).map((action) => (
              <button
                key={action.key}
                className={classNames("matches__candidate-action-item", {
                  "matches__candidate-action-item--selected":
                    tab === action.key,
                })}
                onClick={(e) => {
                  // Prevent the event from bubbling up to the parent
                  e.stopPropagation()
                  setSearchParams({
                    tab: action.key,
                    candidate: selectedCandidate,
                  })
                }}
              >
                {formatMessage({ id: action.name })}
              </button>
            ))}
          </div>
        )

        return (
          <div
            role={"button"}
            className={classNames("matches__candidate", {
              "matches__candidate--selected": selected,
            })}
            key={`Candidate-${candidate.id}`}
            onClick={onCandidateClick}
          >
            <div className="matches__candidate-info">
              {candidateCV?.information ? (
                <span>
                  {`${candidateCV.information.first_name} ${candidateCV.information.last_name}`}
                </span>
              ) : (
                <span className="matches__candidate-resume-not-found">
                  <FormattedMessage id="common.resumeNotFound" />
                </span>
              )}
              <span>{Math.round(candidate.summary_score)}</span>
            </div>
            {actionButtons}
          </div>
        )
      })}
    </div>
  )

  const selectedJobSection = selectedJob && (
    <div>
      <p className={"matches__job-ad matches__job-ad--selected no-border"}>
        {selectedJob.information?.title}
        <span>
          {new Date(selectedJob.created_at).toLocaleDateString()}
          <br />
          {fromNow(selectedJob.created_at, teamLanguage)}
        </span>
      </p>

      <CustomNavLink
        className={(props) =>
          classNames("matches__menu-item", {
            "matches__menu-item--selected": props.isActive,
          })
        }
        to={`/job-match/${selectedJob.id}/candidates`}
        trackingEvent={GA_EVENTS.JOB_MATCHES.SIDE_BAR_CANDIDATE_CVS}
      >
        <FormattedMessage id="sidebar.candidateCVs" />{" "}
        <span>{selectedCVs?.length}</span>
      </CustomNavLink>

      <CustomLink
        className={"matches__menu-item"}
        to={`/job-match/${selectedJob.id}/requirements`}
        trackingEvent={GA_EVENTS.JOB_MATCHES.SIDE_BAR_REQUIREMENTS}
      >
        <FormattedMessage id="sidebar.jobRequirements" />{" "}
        {jobStatus === JOB_STATUS.COMPLETED
          ? <span><FormattedMessage id="sidebar.viewOrEdit" /></span>
          : isFetchingJobInfo
            ? <span ><FormattedMessage id="common.loading" /></span>
            : <span className="continue-here"><FormattedMessage id="sidebar.continueHere" /></span>
        }
      </CustomLink>

      {(isFetchingJobInfo || isDeletingJobs) && (
        <Spinner variant="secondary" />)
      }
      {jobStatus !== JOB_STATUS.COMPLETED && !isFetchingJobInfo &&
          <p className="matches__info matches__info--incomplete">
            <FormattedMessage id="common.matchingIncomplete" />{" "}
            <FormattedMessage id="common.continueAndFindMatches" />
          </p>
      }
      {jobStatus === JOB_STATUS.COMPLETED && !candidates?.length && (
        <p className="matches__info">
          <FormattedMessage id="sidebar.noMatchesFound" />
        </p>
      )}

      {sideBarCandidates}
    </div>
  )

  const toolbarSection = (
    <div className="matches__toolbar">
      <div className="matches__toolbar__row">
        <p className="matches__recent">
          <FormattedMessage id="sidebar.recentJobMatches" />
        </p>

        <input
          className="matches__search"
          value={search}
          placeholder={formatMessage({ id: "common.find" })}
          onChange={(e) => setSearch(e.target.value)}
        />

        {searchSvg}
      </div>

      <div className="matches__toolbar__row">
        <label className="date-label sidebar-label">
          <FormattedMessage id="search.from" />
          <input
            className="date-input sidebar-input"
            type="date"
            value={fromDate || ""}
            onChange={({target}) => setFromDate(target.value)}
            max={new Date().toISOString().split("T")[0]}
          />
        </label>
      </div>

      <div className="matches__toolbar__row">
        <label className="date-label sidebar-label">
          <FormattedMessage id="search.to" />
          <input
            className="date-input sidebar-input"
            type="date"
            value={toDate || today}
            onChange={({target}) => setToDate(target.value)}
            max={new Date().toISOString().split("T")[0]}
          />
        </label>
      </div>
    </div>
  )

  
  const filteredJobs = useMemo(() => {
    if (!storedJobs) return []

    const searchKeyword = search.toLowerCase()
    return storedJobs.filter((job) => {
      const createdAt = new Date(job.created_at);
      const fromDateObj = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
      const toDateObj = toDate ? new Date(`${toDate}T23:59:59`) : null;

      const isWithinDateRange =
        (!fromDateObj || createdAt.getTime() >= fromDateObj.getTime()) &&
        (!toDateObj || createdAt.getTime() <= toDateObj.getTime());

      if (!isWithinDateRange) return false;

      return job.information?.title.toLowerCase().includes(searchKeyword);
    });
  }, [search, storedJobs, fromDate, toDate]);

  const sortedJobs = filteredJobs
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  const handleJobCheckboxChange = (jobId, checked) => {
    const newSelectedJobs = new Set(selectedJobs)
    if (checked) {
      newSelectedJobs.add(jobId)
    } else {
      newSelectedJobs.delete(jobId)
    }
    setSelectedJobs(newSelectedJobs)
  }

  const handleDeleteJobs = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    const jobIds = Array.from(selectedJobs);
    setIsDeletingJobs(true);
    setShowDeleteConfirmModal(false);
    try {
      await deleteJobs(jobIds);
      jobIds.forEach((jobId) => dispatch(removeJob(jobId)));
      setSelectedJobs(new Set());
      
      // Redirect to a remaining job if the current one was deleted
      if (jobIds.includes(jobId)) {
        const remainingJobs = storedJobs.filter(job => !jobIds.includes(job.id));
        if (remainingJobs.length > 0) {
          navigate(`/job-match/${remainingJobs[0].id}/matches`);
        } else {
          navigate('/job-match/matches');
        }
      }
      
      toast.success(formatMessage({ id: "toast.jobsDeletedSuccessfully" }));
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error(formatMessage({ id: "toast.failedToDeleteJobs" }));
      // Optionally show an error message to the user
    } finally {
      setIsDeletingJobs(false);
    }
  };

  const handleSelectAllJobs = () => {
    if (selectedJobs.size === sortedJobs.length) {
      setSelectedJobs(new Set())
    } else {
      setSelectedJobs(new Set(sortedJobs.map((job) => job.id)))
    }
  }

  const disableActions = isDeletingJobs || isFetching || isFetchingJobInfo;
  const jobListActions = sortedJobs.length > 0 && (
    <div className="matches__job-list-actions">
      <button
        className={classNames("btn secondary btn--smaller", {
          disabled: disableActions,
        })}
        disabled={disableActions}
        onClick={handleSelectAllJobs}
      >
        {selectedJobs.size === sortedJobs.length ? (
          <FormattedMessage id="common.deselectAll" />
        ) : (
          <FormattedMessage id="common.selectAll" />
        )}
      </button>

      {selectedJobs.size > 0 && (
        <button
          className={classNames("btn warning btn--smaller", {
            disabled: disableActions,
          })}
          onClick={handleDeleteJobs}
          disabled={disableActions}
        >
          <FormattedMessage id="common.delete" />
        </button>
      )}
    </div>
  )

  const sortedJobList = sortedJobs.map((job) => {
    const isSelected = job.id === jobId
    const isChecked = selectedJobs.has(job.id)
    
    return (
      <div key={`Job-${job.id}`} className={classNames("matches__job-item matches__job-item--hoverable", {
        "matches__job-item--checked": isChecked
      })}>
        <div className="matches__job-checkbox-container" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            value={isChecked}
            onChange={(e) => handleJobCheckboxChange(job.id, e.target.checked)}
            variant="secondary"
            small={true}
          />
        </div>
        <Link
          className={classNames("matches__job-ad", {
            "matches__job-ad--selected": isSelected,
            "matches__job-ad--with-checkbox": true,
          })}
          to={`/job-match/${job.id}/matches`}
        >
          {job.information?.title}
          <span>
            {new Date(job.created_at).toLocaleDateString()}
            <br />
            {fromNow(job.created_at, teamLanguage)}
          </span>
        </Link>
      </div>
    );
  })

  // #endregion ------------- Render Components -------------

  return (
    <div className="matches">
      {isFetching ? (
        <Spinner variant="secondary" />
      ) : storedJobs.length === 0 ? (
        <p className="matches__info">
          <FormattedMessage id="sidebar.noJobMatches" />
        </p>
      ) : (
        <>
          {toolbarSection}
          {selectedJobSection}
          {jobListActions}
          {sortedJobList}
        </>
      )}

      <ConfirmationDialog
        open={showDeleteConfirmModal}
        setOpen={setShowDeleteConfirmModal}
        title={formatMessage({ id: "common.confirmDelete" })}
        message={formatMessage(
          { id: "message.areYouSureDeleteJobs" },
          { jobsLength: selectedJobs.size }
        )}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}

export default MatchesPageSidebar

const searchSvg = (
  <svg
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.3397 19.0625L14.9897 14.7125M17.3397 9.0625C17.3397 13.4808 13.758 17.0625 9.33972 17.0625C4.92144 17.0625 1.33972 13.4808 1.33972 9.0625C1.33972 4.64422 4.92144 1.0625 9.33972 1.0625C13.758 1.0625 17.3397 4.64422 17.3397 9.0625Z"
      stroke="#3FA06E"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)